import { createReducer } from '@reduxjs/toolkit';
import { type VestingClaimHistoryRecord } from '@starly/starly-types';
import {
  vestingLoginResponse,
  vestingReset,
  vestingToggleModal,
  vestingBalanceResponse,
  vestingClaimRequest,
  vestingToggleWalletLoading,
  vestingHistoryResponse,
  vestingHistoryRequest,
  type SupportedBlockchain,
  type VestingWallet,
  vestingLoginRequest,
  vestingToggleLoading,
  vestingToggleRewardLoading,
  vestingRewardBalanceResponse,
} from './vestingActions';

interface VestingSlice {
  wallet?: {
    blockchain: SupportedBlockchain,
    address: string,
    providerId?: string,
  },
  balance?: { [key: string]: VestingWallet }
  rewardBalance?: { [key: string]: VestingWallet }
  walletsInProgress: { [key: string]: boolean },
  currentTime: number,
  history: {
    transactions: VestingClaimHistoryRecord[],
    isLoading: boolean,
  }
  modal: {
    isOpen: boolean,
    error: {
      message: string,
      code?: number,
    }
  }
  isLoading: boolean
  isRewardLoading: boolean
}

const initialState: VestingSlice = {
  wallet: undefined,
  balance: undefined,
  rewardBalance: undefined,
  walletsInProgress: {},
  currentTime: 0,
  history: {
    transactions: [],
    isLoading: false,
  },
  modal: {
    isOpen: false,
    error: {
      message: '',
      code: 0,
    },
  },
  isLoading: false,
  isRewardLoading: false,
};

const vestingReducer = createReducer(
  initialState,
  (builder) => builder
    .addCase(vestingToggleLoading, (state, { payload }) => {
      state.isLoading = payload;
    })
    .addCase(vestingToggleRewardLoading, (state, { payload }) => {
      state.isRewardLoading = payload;
    })
    .addCase(vestingLoginRequest, (state) => {
      state.isLoading = true;
    })
    .addCase(vestingLoginResponse, (state, {
      payload: {
        wallet,
      },
    }) => {
      state.wallet = wallet;
      state.isLoading = false;
    })
    .addCase(vestingBalanceResponse, (state, {
      payload: {
        balance,
        currentTime,
      },
    }) => {
      state.balance = balance;
      state.currentTime = currentTime;
    })
    .addCase(vestingRewardBalanceResponse, (state, {
      payload: {
        balance,
        currentTime,
      },
    }) => {
      state.rewardBalance = balance;
      state.currentTime = currentTime;
    })
    .addCase(vestingClaimRequest, (state, {
      payload: {
        walletId,
      },
    }) => {
      state.walletsInProgress[walletId] = true;
    })
    .addCase(vestingHistoryRequest, (state) => {
      state.history.isLoading = true;
    })
    .addCase(vestingHistoryResponse, (state, {
      payload: {
        transactions,
      },
    }) => {
      state.history.transactions = transactions;
      state.history.isLoading = false;
    })
    .addCase(vestingToggleWalletLoading, (state, {
      payload: {
        walletId,
      },
    }) => {
      state.walletsInProgress[walletId] = false;
    })
    .addCase(vestingReset, (state) => {
      state.wallet = undefined;
      state.balance = undefined;
    })
    .addCase(vestingToggleModal, (state, { payload: { isOpen, errorMessage, errorCode } }) => {
      state.modal.isOpen = isOpen;
      state.modal.error.message = errorMessage && errorMessage.length > 0 ? errorMessage : '';
      state.modal.error.code = errorCode || 0;
    }),
);

export default vestingReducer;
